import React, { useEffect } from 'react';
import { Download, ArrowRight, CheckCircle2 } from 'lucide-react';

export default function AdsPlaybook() {
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const features = [
        "Storm response campaigns that launch within 72 hours of a hail event in your zip code",
        "CompanyCam job-to-ad workflow — turn every completed roof into Facebook creative automatically",
        "Google Ads copy built for roofing: RSA headlines, emergency repair ads, LSA optimization",
        "Retargeting sequences for homeowners who visited your site but didn't book",
        "Seasonal campaign calendar generator — spring inspections, fall readiness, storm season",
        "HubSpot pipeline → Facebook Custom Audience strategy so your ads match where every lead actually is",
    ];

    return (
        <div className="min-h-screen bg-[#0D0D0F] text-[#F0EFF4]">
            {/* Hero */}
            <div className="max-w-4xl mx-auto px-6 pt-20 pb-16 text-center">
                <p className="font-mono text-xs tracking-[0.2em] text-[#7B61FF] uppercase mb-6">
                    Free Download
                </p>
                <h1 className="text-4xl md:text-5xl font-heading font-bold text-white leading-tight mb-6">
                    The RoofClaw Ads Playbook
                </h1>
                <p className="text-xl text-[#F0EFF4]/70 leading-relaxed max-w-2xl mx-auto mb-10">
                    25 copy-paste prompts for running Facebook &amp; Google Ads with your AI marketing agent — without paying an agency $2,000/month to do it badly.
                </p>

                {/* Book Cover Placeholder */}
                <div className="mx-auto w-56 h-72 rounded-2xl overflow-hidden mb-12 shadow-2xl shadow-[#7B61FF]/20 border border-[#7B61FF]/20">
                    <img
                        src="/images/ads-playbook-cover.png"
                        alt="The RoofClaw Ads Playbook"
                        className="w-full h-full object-cover"
                        onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.parentElement.classList.add('flex', 'items-center', 'justify-center', 'bg-[#18181B]');
                            e.target.parentElement.innerHTML = '<div class="text-center p-6"><div class="text-5xl mb-4">📋</div><p class="text-[#7B61FF] font-semibold text-sm">RoofClaw Ads Playbook</p><p class="text-[#F0EFF4]/40 text-xs mt-1">25 Prompts</p></div>';
                        }}
                    />
                </div>
            </div>

            {/* What's Inside */}
            <div className="max-w-3xl mx-auto px-6 pb-16">
                <div className="bg-[#18181B] border border-[#7B61FF]/20 rounded-2xl p-8 mb-10">
                    <p className="font-mono text-xs tracking-[0.2em] text-[#7B61FF] uppercase mb-6">
                        What's Inside
                    </p>
                    <ul className="space-y-4">
                        {features.map((feature, i) => (
                            <li key={i} className="flex items-start gap-3">
                                <CheckCircle2 className="w-5 h-5 text-[#7B61FF] flex-shrink-0 mt-0.5" />
                                <span className="text-[#F0EFF4]/80 leading-relaxed">{feature}</span>
                            </li>
                        ))}
                    </ul>
                </div>

                {/* Who it's for */}
                <div className="mb-10">
                    <p className="text-[#F0EFF4]/50 text-sm text-center leading-relaxed max-w-xl mx-auto">
                        Built for marketing directors and operations leads at roofing companies who run their own ads — not agencies, not consultants. If you know your business and want an AI agent that knows it too, this is your starting point.
                    </p>
                </div>

                {/* Primary CTA */}
                <div className="flex flex-col sm:flex-row gap-4 justify-center mb-6">
                    <a
                        href="/downloads/roofclaw-ads-playbook.md"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center justify-center gap-2 bg-[#7B61FF] hover:bg-[#6B51EF] text-white font-semibold px-8 py-4 rounded-xl transition-colors duration-200"
                    >
                        <Download className="w-5 h-5" />
                        <span>
                            Get the Prompts
                            <span className="block text-xs font-normal opacity-75">(delivered in markdown format for your RoofClaw to save tokens)</span>
                        </span>
                    </a>
                </div>

                {/* Divider */}
                <div className="my-12 border-t border-[#F0EFF4]/10" />

                {/* Secondary CTA */}
                <div className="text-center">
                    <p className="text-[#F0EFF4]/60 text-base mb-2">
                        Ready to put these prompts inside a live marketing agent?
                    </p>
                    <p className="text-[#F0EFF4]/40 text-sm mb-6">
                        The AI Readiness Session maps your roofing business to a full agent build plan. 60 minutes with Adam. $800 — credits toward the full build if you move forward.
                    </p>
                    <a
                        href="https://www.roofingbusinesspartner.com/meetings/adamsandlinks/roofclaw-ai-readiness"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center justify-center gap-2 border border-[#7B61FF] hover:bg-[#7B61FF]/10 text-[#7B61FF] font-semibold px-8 py-4 rounded-xl transition-colors duration-200"
                    >
                        Book Your AI Readiness Session
                        <ArrowRight className="w-4 h-4" />
                    </a>
                </div>
            </div>
        </div>
    );
}
